import { useMemo } from "react";
import { TrackAnalyticsEvent } from "../../services/Analytics";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";
import { EditFormDialog } from "./EditFormDialog";
import ClinicInformation from "./NewReqClinicInformation";
import PatientInformation from "./NewReqPatientInformation";
import {
  EditWindowCountdown,
  OrderDetailsMetadata,
} from "./OrderDetailsDialog";
import { editOrder as submitEditOrder } from "./Services/NewReqService";
import { convertNewReqTypeToForm } from "./Services/helpers";
import { hasEditOrderValidationError } from "./helpers";
import {
  buildChangedEditOrderPayload,
  getEditOrderErrorMessage,
} from "./orderEdit";
import { NewRequisitionType } from "./types";

export const NewReqEditOrderDialog: React.FC<{
  order?: NewRequisitionType;
  onClose: () => void;
  onSubmitted: () => Promise<void> | void;
}> = ({ order, onClose, onSubmitted }) => {
  const { auth, setLoggedOut } = useAuthenticationHook();
  const originalEditForm = useMemo(
    () => (order ? convertNewReqTypeToForm(order) : undefined),
    [order],
  );

  if (!auth || !order || !originalEditForm) return null;

  return (
    <EditFormDialog
      id="edit-order-dialogue"
      title={`EDITING ORDER ${order.unity_complete_id}`}
      titleExtra={
        order.edit_window_expires_at && (
          <EditWindowCountdown expiresAt={order.edit_window_expires_at} />
        )
      }
      submitLabel="SAVE CHANGES"
      originalForm={originalEditForm}
      getChanges={(form) =>
        buildChangedEditOrderPayload(form, originalEditForm)
      }
      getValidationError={hasEditOrderValidationError}
      onSubmitClicked={() =>
        TrackAnalyticsEvent(
          "ProviderElectronicOrderEditSaveClicked",
          auth.token,
          auth.username,
          {
            source: "edit_order_dialog",
            unity_complete_id: order.unity_complete_id,
          },
        )
      }
      onSubmit={async (form) => {
        const result = await submitEditOrder(
          order.unity_complete_id,
          buildChangedEditOrderPayload(form, originalEditForm),
          auth.token,
          setLoggedOut,
        );
        if (!result.ok) return getEditOrderErrorMessage(result);
        await onSubmitted();
      }}
      onClose={onClose}
    >
      <OrderDetailsMetadata
        orderNumber={order.unity_complete_id}
        created={order}
      />
      <ClinicInformation
        preflight={false}
        submittedRequisition={originalEditForm}
        isEditMode
      />
      <PatientInformation
        preflight={false}
        submittedRequisition={originalEditForm}
        isEditMode
      />
    </EditFormDialog>
  );
};
